import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import MarkdownRender from 'LuxKit/MarkdownRender'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { formatOccupantsShort, type AdultLabel } from 'lib/offer/occupancyUtils'

import type { RoomPackageAmenityGroup } from './AccommodationAmenitiesList'
import AccommodationAmenitiesList, {
  extractBedbankRoomPackageAmenityGroups,
  extractHotelRoomPackageAmenityGroups,
  extractPropertyRoomPackageAmenityGroups,
} from './AccommodationAmenitiesList'
import { mapRoomPackage } from './roomPackageUtils'
import AccommodationExtraGuestPoliciesTable from './AccommodationExtraGuestPoliciesTable'
import AccommodationMaximumRoomCapacities from './AccommodationMaximumRoomCapacities'
import AccommodationPackageInclusions from './AccommodationPackageInclusions'
import AccommodationBeddingDetails from './AccommodationBeddingDetails'
import AccommodationAdditionalPolicyNotes, { type AdditionalPolicyNote } from './AccommodationAdditionalPolicyNotes'
import type {
  ExtraGuestGeneratedPolicies,
  IconTextItem,
} from './AccommodationAmenitiesAndPoliciesContent.types'

export type {
  ExtraGuestGeneratedPolicies,
  ExtraGuestPolicyItem,
  ExtraGuestTypeLabel,
  IconTextItem,
} from './AccommodationAmenitiesAndPoliciesContent.types'

interface RoomCapacityObject {
  adults: number
  children: number
  infants: number
  teenagers: number
}

function isRoomCapacityObject(value: unknown): value is RoomCapacityObject {
  return typeof value === 'object' && value !== null && 'adults' in value
}

function normalizeBeddingConfiguration(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (typeof value === 'object' && value !== null && 'bedding' in value) {
    const bedding = (value as { bedding?: unknown }).bedding
    return typeof bedding === 'string' ? bedding : undefined
  }
  return undefined
}

function mergeCapacityEntries(items: Array<RoomCapacityObject>, adultLabel: AdultLabel = 'adult'): Array<string> {
  return items.map(item => formatOccupantsShort(item, undefined, adultLabel))
}

function wrapUpGuestCapacityCombos(items: Array<RoomCapacityObject>, adultLabel: AdultLabel = 'adult'): Array<string> {
  if (!items.length) return []

  const getTotalGuests = ({ adults, teenagers, children, infants }: RoomCapacityObject): number =>
    adults + teenagers + children + infants

  const maxGuests = Math.max(...items.map(getTotalGuests))

  // Only collapse when all entries represent the same "max guests" capacity.
  if (items.some((i) => getTotalGuests(i) !== maxGuests)) {
    return mergeCapacityEntries(items, adultLabel)
  }

  const activeExtraGuestTypes = (['teenagers', 'children', 'infants'] as const).filter((guestType) =>
    items.some((item) => item[guestType] > 0),
  )

  const toKey = ({ adults, teenagers, children, infants }: RoomCapacityObject): string => {
    const values: Array<number> = [adults]
    if (activeExtraGuestTypes.includes('teenagers')) values.push(teenagers)
    if (activeExtraGuestTypes.includes('children')) values.push(children)
    if (activeExtraGuestTypes.includes('infants')) values.push(infants)
    return values.join('|')
  }

  const actualCombos = new Set(items.map(toKey))

  // Only collapse when there is one of each combo (no duplicates).
  if (actualCombos.size !== items.length) {
    return mergeCapacityEntries(items, adultLabel)
  }

  // "All combos" means every way to split `maxGuests` across guest types, with at least 1 adult.
  // Guest types that are always 0 across all combos should not be considered.
  const expectedCombos = new Set<string>()

  const buildKey = (adults: number, extrasByType: Partial<Record<(typeof activeExtraGuestTypes)[number], number>>) => {
    const values: Array<number> = [adults]
    if (activeExtraGuestTypes.includes('teenagers')) values.push(extrasByType.teenagers ?? 0)
    if (activeExtraGuestTypes.includes('children')) values.push(extrasByType.children ?? 0)
    if (activeExtraGuestTypes.includes('infants')) values.push(extrasByType.infants ?? 0)
    return values.join('|')
  }

  if (activeExtraGuestTypes.length === 0) {
    expectedCombos.add(buildKey(maxGuests, {}))
  } else if (activeExtraGuestTypes.length === 1) {
    const [type] = activeExtraGuestTypes
    for (let adults = 1; adults <= maxGuests; adults++) {
      const remaining = maxGuests - adults
      expectedCombos.add(buildKey(adults, { [type]: remaining }))
    }
  } else if (activeExtraGuestTypes.length === 2) {
    const [typeA, typeB] = activeExtraGuestTypes
    for (let adults = 1; adults <= maxGuests; adults++) {
      const remaining = maxGuests - adults
      for (let a = 0; a <= remaining; a++) {
        expectedCombos.add(buildKey(adults, { [typeA]: a, [typeB]: remaining - a }))
      }
    }
  } else {
    for (let adults = 1; adults <= maxGuests; adults++) {
      const remaining = maxGuests - adults
      for (let teenagers = 0; teenagers <= remaining; teenagers++) {
        for (let children = 0; children <= remaining - teenagers; children++) {
          const infants = remaining - teenagers - children
          expectedCombos.add(
            buildKey(adults, {
              teenagers,
              children,
              infants,
            }),
          )
        }
      }
    }
  }

  if (actualCombos.size !== expectedCombos.size) {
    return mergeCapacityEntries(items, adultLabel)
  }

  for (const combo of expectedCombos) {
    if (!actualCombos.has(combo)) {
      return mergeCapacityEntries(items, adultLabel)
    }
  }

  const intl = getCurrentIntl()
  return [
    intl.formatMessage(
      {
        id: 'U4TaOsaIcAMwAnHEIP8v',
        defaultMessage: '{count, plural, one {# guest} other {# guests}}',
        description:
          'Collapsed maximum room capacity label shown as a single guest count when all splits are available',
      },
      { count: maxGuests },
    ),
  ]
}

export function parseExtraGuestGeneratedPolicies(policyDescription?: string): ExtraGuestGeneratedPolicies | undefined {
  if (!policyDescription) return undefined

  try {
    const parsed = JSON.parse(policyDescription)
    if (
      Array.isArray(parsed?.extraGuestGeneratedPolicyItems) &&
      Array.isArray(parsed?.maximumRoomCapacities) &&
      Array.isArray(parsed?.packageInclusions) &&
      Array.isArray(parsed?.additionalPolicyNotes)
    ) {
      const capacitiesAdultLabel: AdultLabel = parsed.displayCapacitiesAdultsAsGuests ? 'guest' : 'adult'
      const inclusionsAdultLabel: AdultLabel = parsed.displayInclusionsAdultsAsGuests ? 'guest' : 'adult'

      return {
        ...parsed,
        maximumRoomCapacities: isRoomCapacityObject(parsed.maximumRoomCapacities[0])
          ? wrapUpGuestCapacityCombos(parsed.maximumRoomCapacities, capacitiesAdultLabel)
          : parsed.maximumRoomCapacities,
        packageInclusions: isRoomCapacityObject(parsed.packageInclusions[0])
          ? wrapUpGuestCapacityCombos(parsed.packageInclusions, inclusionsAdultLabel)
          : parsed.packageInclusions,
        beddingConfiguration: normalizeBeddingConfiguration(parsed.beddingConfiguration),
        beddingConfigurationExtras: Array.isArray(parsed.beddingConfigurationExtras)
          ? parsed.beddingConfigurationExtras.map((item: string | { bedding: string; icon?: string }): IconTextItem =>
              typeof item === 'string' ? { text: item } : { text: item.bedding, icon: item.icon || undefined },
            )
          : [],
        extraGuestGeneratedPolicyItems: parsed.extraGuestGeneratedPolicyItems.map(
          (item: Record<string, unknown> & { inclusions: Array<string | { inclusion: string; icon?: string }> }) => ({
            ...item,
            inclusions: item.inclusions.map((inc: string | { inclusion: string; icon?: string }): IconTextItem =>
              typeof inc === 'string' ? { text: inc } : { text: inc.inclusion, icon: inc.icon || undefined },
            ),
          }),
        ),
        additionalPolicyNotes: parsed.additionalPolicyNotes
          .filter((note: AdditionalPolicyNote) => typeof note === 'string' || !note.pinned)
          .map((note: AdditionalPolicyNote) => typeof note === 'string' ? note : note.text),
        pinnedNotes: parsed.additionalPolicyNotes
          .filter((note: AdditionalPolicyNote) => typeof note !== 'string' && note.pinned)
          .map((note: AdditionalPolicyNote & { text: string }) => note.text),
      }
    }
    return undefined
  } catch {
    return undefined
  }
}

interface AccommodationPolicyDescriptionProps {
  policyDescription: string
  extraGuestGeneratedPolicies?: ExtraGuestGeneratedPolicies | null
  beddingDetails?: string
  fontSize?: ComponentProps<typeof MarkdownRender>['fontSize']
  type?: ComponentProps<typeof MarkdownRender>['type']
  className?: string
}

export function AccommodationPolicyDescription({
  policyDescription,
  extraGuestGeneratedPolicies,
  beddingDetails,
  fontSize,
  type = 'compact',
  className,
}: AccommodationPolicyDescriptionProps) {
  const parsedExtraGuestGeneratedPolicies = useMemo(() => {
    if (extraGuestGeneratedPolicies !== undefined) return extraGuestGeneratedPolicies ?? undefined

    return parseExtraGuestGeneratedPolicies(policyDescription)
  }, [extraGuestGeneratedPolicies, policyDescription])

  if (!parsedExtraGuestGeneratedPolicies) {
    return <MarkdownRender className={className} content={policyDescription} fontSize={fontSize} type={type} />
  }

  const beddingDetailsContent = parsedExtraGuestGeneratedPolicies.beddingConfiguration ?? beddingDetails

  return (
    <Group className={className} direction="vertical" gap={12}>
      {parsedExtraGuestGeneratedPolicies.maximumRoomCapacities.length > 0 && <div>
        <AccommodationMaximumRoomCapacities maximumRoomCapacities={parsedExtraGuestGeneratedPolicies.maximumRoomCapacities} />
      </div>}
      {parsedExtraGuestGeneratedPolicies.pinnedNotes.length > 0 && <div>
        <AccommodationAdditionalPolicyNotes additionalPolicyNotes={parsedExtraGuestGeneratedPolicies.pinnedNotes} highlighted />
      </div>}
      {parsedExtraGuestGeneratedPolicies.packageInclusions.length > 0 && <div>
        <AccommodationPackageInclusions packageInclusions={parsedExtraGuestGeneratedPolicies.packageInclusions} />
      </div>}
      {parsedExtraGuestGeneratedPolicies.extraGuestGeneratedPolicyItems.length > 0 && <div>
        <AccommodationExtraGuestPoliciesTable extraGuestGeneratedPolicyItems={parsedExtraGuestGeneratedPolicies.extraGuestGeneratedPolicyItems} />
      </div>}
      {beddingDetailsContent && <div>
        <AccommodationBeddingDetails beddingDetails={beddingDetailsContent} extraGuestGeneratedPolicies={parsedExtraGuestGeneratedPolicies} />
      </div>}
      {parsedExtraGuestGeneratedPolicies.additionalPolicyNotes.length > 0 && <div>
        <AccommodationAdditionalPolicyNotes additionalPolicyNotes={parsedExtraGuestGeneratedPolicies.additionalPolicyNotes} />
      </div>}
    </Group>
  )
}

interface RoomPackageDetails {
  amenityGroups: Array<RoomPackageAmenityGroup>
  policyDescription?: string
  beds?: string
}

function extractPropertyRoomPackageAmenitiesAndPoliciesDetails(roomPackage: App.Property.Room, orWord: string): RoomPackageDetails {
  return {
    amenityGroups: extractPropertyRoomPackageAmenityGroups(roomPackage),
    policyDescription: roomPackage.policy,
    beds: roomPackage.bedGroupDescriptions.join(` ${orWord} `) || undefined,
  }
}

function extractHotelRoomPackageAmenitiesAndPoliciesDetails(roomPackage: App.HotelPackage, orWord: string): RoomPackageDetails {
  return {
    amenityGroups: extractHotelRoomPackageAmenityGroups(roomPackage),
    policyDescription: roomPackage.roomPolicyDescription,
    beds: (roomPackage.roomType.bedGroups ?? []).map((b) => b.description).join(` ${orWord} `),
  }
}

function extractBedbankRoomPackageAmenitiesAndPoliciesDetails(roomPackage: App.BedbankPackage): RoomPackageDetails {
  const features = new Map(roomPackage.features.map((f) => ([f.id, f.text])))

  return {
    amenityGroups: extractBedbankRoomPackageAmenityGroups(roomPackage),
    policyDescription: undefined,
    beds: features.get('bed'),
  }
}

interface Props {
  roomPackage: App.Property.Room | App.HotelPackage | App.BedbankPackage
  headingVariant?: ComponentProps<typeof Heading>['variant']
  policyFontSize?: ComponentProps<typeof MarkdownRender>['fontSize']
  hideAmenities?: boolean
}

function AccommodationAmenitiesAndPoliciesContent(props: Props) {
  const {
    roomPackage,
    headingVariant = 'heading6',
    policyFontSize,
    hideAmenities = false,
  } = props

  const intl = useIntl()

  const orWord = intl.formatMessage({
    id: 'GzXJi62SQuqpWHf7Jmwj',
    defaultMessage: 'OR',
    description: 'Separator between alternative bed group options (e.g. "1 King Bed OR 2 Single Beds")',
  })

  const details = useMemo(() => {
    return mapRoomPackage(
      roomPackage,
      (room) => extractPropertyRoomPackageAmenitiesAndPoliciesDetails(room, orWord),
      (pkg) => extractHotelRoomPackageAmenitiesAndPoliciesDetails(pkg, orWord),
      extractBedbankRoomPackageAmenitiesAndPoliciesDetails,
    )
  }, [roomPackage, orWord])

  const extraGuestGeneratedPolicies = useMemo(() => {
    return parseExtraGuestGeneratedPolicies(details.policyDescription)
  }, [details.policyDescription])

  const roomAmenitiesHeading = intl.formatMessage({
    id: 'O0QKcNjGpIKeLndM3utX',
    defaultMessage: 'Room Amenities',
    description: 'Heading for the room amenities section within the room details content.',
  })

  const guestPolicyHeading = intl.formatMessage({
    id: 'iml0NoUKr2xKRNWRSyo5',
    defaultMessage: 'Guest Policy',
    description: 'Heading for the guest policy section within the room details content.',
  })

  const roomPoliciesHeading = intl.formatMessage({
    id: '_IjRrOdK6E3y0RVEFVhr',
    defaultMessage: 'Room Policies',
    description: 'Heading for the room policies section within the room details content.',
  })

  const policyDescription = details.policyDescription
  const hasNewPolicyUI = !!extraGuestGeneratedPolicies && !!policyDescription
  const hasFallbackPolicy = !hasNewPolicyUI && !!policyDescription

  return <>
    {!!details.amenityGroups.length && !hideAmenities && <Group direction="vertical" gap={12}>
      <Heading variant={headingVariant}>{roomAmenitiesHeading}</Heading>
      <AccommodationAmenitiesList amenities={details.amenityGroups} />
    </Group>}
    {hasNewPolicyUI && <Group direction="vertical" gap={12}>
      <Heading variant={headingVariant}>{guestPolicyHeading}</Heading>
      <AccommodationPolicyDescription
        policyDescription={policyDescription}
        extraGuestGeneratedPolicies={extraGuestGeneratedPolicies}
        beddingDetails={details.beds}
        fontSize={policyFontSize}
      />
    </Group>}
    {hasFallbackPolicy && <Group direction="vertical" gap={12}>
      <Heading variant={headingVariant}>{roomPoliciesHeading}</Heading>
      <AccommodationPolicyDescription
        policyDescription={policyDescription}
        extraGuestGeneratedPolicies={extraGuestGeneratedPolicies ?? null}
        fontSize={policyFontSize}
      />
    </Group>}
  </>
}

export default AccommodationAmenitiesAndPoliciesContent
